import React, { useCallback, useState } from "react";
import {
  NOTIFICATION_STATUSES,
  useNotifications,
} from "@adsk/alloy-react-notification";
import { ModalConfirmation } from "@adsk/alloy-react-modal";
import { useTranslations } from "@adsk/acc-issues-translations";
import { useDispatch } from "@adsk/acc-issues-core/state/redux/hooks";
import { bulkDeleteIssuesSuccess } from "@adsk/acc-issues-core/state/redux/ducks/issuesCollection";
import {
  trackIssueBulkDelete,
  trackIssueBulkDeleteFailure,
  trackIssueBulkDeleteFromBulkEditSidePanel,
  trackIssueBulkDeletePartialFailure,
} from "../utils/analytics/issueDeletionTracking";
import { IssueFailureInfo } from "@adsk/acc-issues-types";
import { IssueBulkActionPartialFailureModal } from "./IssueBulkActionPartialFailureModal";
import { IssueBulkDeleteConfirmationOpenedFrom } from "./IssueBulkDeleteConfirmationOpenedFrom";
import { useCurrentRightPanel } from "../IssuesTab/IssueLog/useCurrentRightPanel";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import { getIssuesPagination } from "@adsk/acc-issues-core/state/redux/ducks/issuesCollection";
import { useReplaceIssuesFetchParams } from "@adsk/acc-issues-core/utils/hooks/queryParams";
import { IssueDeletionCommentField } from "../IssueDetailPanel/IssueDetailTabPanel/IssueDeletionCommentField";
import { useIssueDeletionComment } from "../IssueDetailPanel/IssueDetailTabPanel/hooks/IssueDeleteHooks/useIssueDeletionComment";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@adsk/acc-issues-core/utils/featureFlags";
import { useBatchDeleteIssues } from "../hooks/useBatchDeleteIssues";
export const IssueBulkDeleteConfirmation = ({
  issueUids,
  onClose,
  isOpen,
  onBeginDeletion,
  openedFrom,
  onSuccessfulDeletion,
}: {
  issueUids: string[];
  onClose: () => void;
  isOpen: boolean;
  onBeginDeletion?: () => void;
  onSuccessfulDeletion?: () => void;
  openedFrom: IssueBulkDeleteConfirmationOpenedFrom;
}) => {
  const { T } = useTranslations();
  const { showNotification } = useNotifications();
  const dispatch = useDispatch();
  // partialFailureModalData as null means do not show the modal
  const [partialFailureModalData, setPartialFailureModalData] =
    useState<IssueFailureInfo[] | null>(null);
  const { activeIssueId, closePanel } = useCurrentRightPanel();
  const issuesPagination = useSelector(getIssuesPagination)!;
  const replaceQueryParams = useReplaceIssuesFetchParams();
  const batchDeleteIssues = useBatchDeleteIssues();

  const {
    issueDeletionComment,
    setIssueDeletionComment,
    isAbleToDeleteWithComment,
    maxCommentLength,
    isCommentTruncated,
  } = useIssueDeletionComment();

  const { value: dontDeleteIssueMarkups } = useFeatureFlag(
    FeatureFlag.DontDeleteIssueMarkups
  );

  const { value: isAbleToRestoreIssues } = useFeatureFlag(
    FeatureFlag.RestoreDeletedIssues
  );

  const handleDelete = useCallback(async () => {
    const onDeleteSuccess = () => {
      setIssueDeletionComment("");
      showNotification({
        status: NOTIFICATION_STATUSES.SUCCESS,
        message: T.translate("issueBulkEdit.deleteSuccessMessage"),
        dismissible: true,
      });
      if (openedFrom === IssueBulkDeleteConfirmationOpenedFrom.TopBar) {
        trackIssueBulkDelete({ count: issueUids.length });
      }

      if (openedFrom === IssueBulkDeleteConfirmationOpenedFrom.BulkEditPanel) {
        trackIssueBulkDeleteFromBulkEditSidePanel({
          selectedCount: issueUids.length,
        });
      }
      // CLose the side panel if the active issue was deleted
      if (activeIssueId && issueUids.includes(activeIssueId)) {
        closePanel();
      }

      // Reset offset instead of showing an empty page
      const newIssuesLength = issuesPagination.totalResults - issueUids.length;
      const isCurrentPageEmptyAfterDelete =
        issuesPagination.offset >= newIssuesLength;
      if (isCurrentPageEmptyAfterDelete) {
        replaceQueryParams({ offset: 0 });
      }
      onSuccessfulDeletion && onSuccessfulDeletion();
    };
    const onDeletePartialFailure = (failureInfo: IssueFailureInfo[]) => {
      showNotification({
        status: NOTIFICATION_STATUSES.WARNING,
        message: T.translate("issueBulkEdit.deletePartialFailure.description", {
          failureCount: failureInfo.length,
        }),
      });
      setPartialFailureModalData(failureInfo);
      trackIssueBulkDeletePartialFailure({
        successCount: issueUids.length - failureInfo.length,
        failureCount: failureInfo.length,
      });
    };
    const onDeleteFailure = () => {
      showNotification({
        status: NOTIFICATION_STATUSES.ERROR,
        message: T.translate("issueBulkEdit.deleteFailureMessage"),
        dismissible: true,
      });
      trackIssueBulkDeleteFailure({ count: issueUids.length });
    };
    onBeginDeletion && onBeginDeletion();
    onClose();
    batchDeleteIssues({
      issueUids,
      comment: issueDeletionComment,
      onSuccess: onDeleteSuccess,
      onPartialFailure: onDeletePartialFailure,
      onFailure: onDeleteFailure,
      shouldDeleteMarkups: !dontDeleteIssueMarkups,
    });
  }, [
    onBeginDeletion,
    onClose,
    issueUids,
    issueDeletionComment,
    dontDeleteIssueMarkups,
    setIssueDeletionComment,
    showNotification,
    T,
    openedFrom,
    activeIssueId,
    issuesPagination.totalResults,
    issuesPagination.offset,
    closePanel,
    replaceQueryParams,
    onSuccessfulDeletion,
    batchDeleteIssues,
  ]);

  const handleClose = useCallback(() => {
    setIssueDeletionComment("");
    onClose();
  }, [onClose, setIssueDeletionComment]);

  let deleteMessage;
  if (isAbleToRestoreIssues) {
    deleteMessage = T.translate(
      "issueBulkEdit.bulkDeleteWithCommentModalMessageWithRestore",
      {
        selectedCount: issueUids.length,
      }
    );
  } else if (isAbleToDeleteWithComment) {
    deleteMessage = T.translate(
      "issueBulkEdit.bulkDeleteWithCommentModalMessage",
      {
        selectedCount: issueUids.length,
      }
    );
  } else {
    deleteMessage = T.translate("issueBulkEdit.bulkDeleteModalMessage", {
      selectedCount: issueUids.length,
    });
  }

  return (
    <>
      <ModalConfirmation
        style={{}}
        show={isOpen}
        title={T.translate("issueBulkEdit.bulkDeleteModalTitle") as string}
        message={
          <>
            <span>{deleteMessage}</span>
            {isAbleToDeleteWithComment && (
              <IssueDeletionCommentField
                value={issueDeletionComment}
                onChange={setIssueDeletionComment}
                maxCommentLength={maxCommentLength}
                isCommentTruncated={isCommentTruncated}
              />
            )}
          </>
        }
        onConfirm={handleDelete}
        onClose={handleClose}
        confirmationButtonLabel={
          T.translate(
            // should be "Delete issues" -- is same as title so we re-use that translation
            "issueBulkEdit.bulkDeleteModalTitle"
          ) as string
        }
      />

      <IssueBulkActionPartialFailureModal
        actionType="delete"
        failureInfo={partialFailureModalData}
        onAcknowledgement={() => {
          /*
           * as currently architected, there are two slightly odd but important things about this.
           * 1) this is being called after acknowledging the modal because otherwise the modal won't
           *    show up because deletion trigggrs a log refresh, which closes the modal
           * 2) it's important that we trigger a success action on ALL of the originally selected issueUids,
           *    even though some did not go through. This is because otherwise
           *    the failed ones will remain selected when the log refreshes, and we don't want that
           *    -- mostly because the refresh will probably remove them from the log since the most likely
           *    reason that they failed is because their permissions
           *    changed or they were deleted such that they aren't visible anymore.
           *    Ideally, they would be automatically deselected as part of the log refresh logic, but that's
           *    not how it works at present. So we need to dispatch they all succeeded to deselect them.
           **/
          dispatch(
            bulkDeleteIssuesSuccess({
              issueUids,
              shouldDeleteMarkups: !dontDeleteIssueMarkups,
            })
          );
          setPartialFailureModalData(null);
          onClose();
        }}
      />
    </>
  );
};
